import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import type { EnvConfig } from './types/env';

declare global {
  interface Window {
    ENV: EnvConfig;
  }
}

// 🔧 注入環境變量 - 優先使用 env-config.js 中的值，避免覆蓋
// 如果 env-config.js 已經設定了 window.ENV，則合併而不是覆蓋
window.ENV = {
  ...window.ENV, // 保留 env-config.js 注入的值
  VITE_API_BASE_URL: window.ENV?.VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL,
  VITE_GOOGLE_API_KEY: window.ENV?.VITE_GOOGLE_API_KEY || import.meta.env.VITE_GOOGLE_API_KEY,
  VITE_GOOGLE_CLIENT_ID: window.ENV?.VITE_GOOGLE_CLIENT_ID || import.meta.env.VITE_GOOGLE_CLIENT_ID,
  VITE_GOOGLE_MAPS_API_KEY: window.ENV?.VITE_GOOGLE_MAPS_API_KEY || import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
  VITE_GOOGLE_OAUTH_SCOPES: window.ENV?.VITE_GOOGLE_OAUTH_SCOPES || import.meta.env.VITE_GOOGLE_OAUTH_SCOPES,
  VITE_FACEBOOK_APP_ID: window.ENV?.VITE_FACEBOOK_APP_ID || import.meta.env.VITE_FACEBOOK_APP_ID,
  VITE_FACEBOOK_APP_SECRET: window.ENV?.VITE_FACEBOOK_APP_SECRET || import.meta.env.VITE_FACEBOOK_APP_SECRET,
  VITE_BASE_URL: window.ENV?.VITE_BASE_URL || import.meta.env.VITE_BASE_URL,
  VITE_FIREBASE_API_KEY: window.ENV?.VITE_FIREBASE_API_KEY || import.meta.env.VITE_FIREBASE_API_KEY,
  VITE_FIREBASE_AUTH_DOMAIN: window.ENV?.VITE_FIREBASE_AUTH_DOMAIN || import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  VITE_FIREBASE_PROJECT_ID: window.ENV?.VITE_FIREBASE_PROJECT_ID || import.meta.env.VITE_FIREBASE_PROJECT_ID,
  VITE_FIREBASE_STORAGE_BUCKET: window.ENV?.VITE_FIREBASE_STORAGE_BUCKET || import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  VITE_FIREBASE_MESSAGING_SENDER_ID: window.ENV?.VITE_FIREBASE_MESSAGING_SENDER_ID || import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  VITE_FIREBASE_APP_ID: window.ENV?.VITE_FIREBASE_APP_ID || import.meta.env.VITE_FIREBASE_APP_ID,
  VITE_FIREBASE_MEASUREMENT_ID: window.ENV?.VITE_FIREBASE_MEASUREMENT_ID || import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
  VITE_APP_VERSION: window.ENV?.VITE_APP_VERSION || import.meta.env.VITE_APP_VERSION,
} as EnvConfig;

console.log('🔧 main.tsx 環境變數合併完成:', {
  VITE_API_BASE_URL: window.ENV.VITE_API_BASE_URL,
  VITE_APP_VERSION: window.ENV.VITE_APP_VERSION
});

// ═══════════════════════════════════════════════════════════════════════
// 🌐 Canonical Domain Guard
// ═══════════════════════════════════════════════════════════════════════
if (typeof window !== 'undefined') {
  const canonicalHost = 'checkplay.me';
  const allowedHosts = new Set([
    canonicalHost,
    `www.${canonicalHost}`,
    'localhost',
    '127.0.0.1'
  ]);
  const currentHost = window.location.host;
  const isDev = import.meta.env.DEV;
  const isCanonical = allowedHosts.has(currentHost);
  const isRunAppDomain = currentHost.endsWith('.run.app');

  if (!isDev && (!isCanonical || isRunAppDomain)) {
    const targetUrl = `https://${canonicalHost}${window.location.pathname}${window.location.search}${window.location.hash}`;
    console.log('[CanonicalRedirect] 重新導向至正式網域:', {
      from: window.location.href,
      to: targetUrl
    });
    window.location.replace(targetUrl);
  }
}

// 🔧 修復：移除 Clerk 配置檢查，因為應用使用 Firebase
// const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
// if (!PUBLISHABLE_KEY) {
//   throw new Error("Missing Clerk Publishable Key");
// }

// ═══════════════════════════════════════════════════════════════════════
// 🔧 開發環境：抑制特定的 Console 警告
// ═══════════════════════════════════════════════════════════════════════
if (import.meta.env.DEV) {
  const originalWarn = console.warn;
  const originalError = console.error;
  
  // 抑制 console.warn 中的特定警告
  console.warn = (...args: any[]) => {
    const message = String(args[0] || '');
    
    // 忽略 MUI Tooltip disabled 警告
    if (message.includes('disabled') && message.includes('Tooltip')) {
      return;
    }
    
    // 忽略 WebSocket 連接警告（Vite HMR 熱模塊替換）
    if (message.includes('WebSocket')) {
      return;
    }
    
    originalWarn.apply(console, args);
  };
  
  // 抑制 console.error 中的特定錯誤
  console.error = (...args: any[]) => {
    const message = String(args[0] || '');
    
    // 忽略 MUI Tooltip disabled 錯誤
    if (message.includes('disabled') && message.includes('Tooltip')) {
      return;
    }
    
    originalError.apply(console, args);
  };
  
  console.log('✅ 開發環境：已啟用 Console 警告過濾');
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  // 🔧 修復：暫時禁用 StrictMode 來解決性能問題
  // <React.StrictMode>
    <App />
  // </React.StrictMode>
); 